import { useCallback, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import Dotdotdot from 'react-dotdotdot'
import removeMd from 'remove-markdown'

import * as TokenSale from '../../contracts/master'
import tempFeaturedProjectImage from './temp-featured-project-image.png'
import Big from 'big.js'
import { USDB_DECIMALS } from '../../config'
import useCountdownTimer, {
  calculateTimeLeft,
} from '../../hooks/useCountdownTimer'
import useWeb3 from '../../hooks/useWeb3'
import LiveLoaderCircle from '../../page_components/Home/parts/LiveLoaderCircle'
import routes from '../../router/routes'
import { IPool, Media } from '../../types/reducerTypes'
import { amountFromWei } from '../../utils/amount'
import { countProgress } from '../../utils/progress'
import ProgressBar from './ProgressBar'
import { Box } from '../UI/Box'
import LoadingState from './LoadingState'
import {
  DescriptionText,
  Image,
  Logo,
  OpeningLabel,
  ProgressContainer,
  StyledLink,
} from './styled'

import { ForceFullWidth } from '../Hero/styled'
import { MaxWidth } from '../Page/styles'
import { ethers } from 'ethers'

const StatsBox = styled(Box)`
  @media screen and (max-width: 1000px) {
    flex: unset;
  }
`

export const StatWrapper = styled.div<{ borderColor?: string }>`
  --button-border-color: ${({ theme, borderColor }) =>
    borderColor ?? theme.colors.brandPrimaryText};

  height: 126px;
  max-width: 32%;
  border: 1px solid var(--button-border-color);

  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: space-evenly;
  flex-grow: 1;

  padding: 2% 4%;
`

const StatTitle = styled.p<{ nameColor?: string }>`
  color: ${({ theme, nameColor }) =>
    nameColor ?? theme.colors.brandPrimaryText};
  font-family: 'Space Grotesk';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5; /* 200% */
  letter-spacing: 0.6px;
  text-transform: uppercase;

  @media screen and (max-width: 1000px) {
    font-size: 11px;
    letter-spacing: 0.39px;
  }
`

const StatValue = styled.p<{ valueColor?: string }>`
  color: ${({ theme, valueColor }) =>
    valueColor ?? theme.colors.brandPrimaryText};
  font-variant-numeric: lining-nums tabular-nums;
  font-family: 'Space Grotesk';
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 1;

  @media screen and (max-width: 1000px) {
    font-size: 15px;
  }
`

const CardContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.brandPrimary};
  mask: url(/cutout.svg) no-repeat;
  mask-size: contain;
  mask-position: center;

  @media screen and (max-width: 1000px) {
    mask: url(/cutout-mobile.svg) no-repeat;
    max-width: 344px;
    min-height: 571px;
  }
`

const CardImageContainer = styled(Box)`
  @media screen and (max-width: 1000px) {
    max-width: 344px;
    max-height: 274px;
  }
`

const CardContentContainer = styled(Box)`
  padding: 0 6% 0 0;
  @media screen and (max-width: 1000px) {
    max-width: 344px;
    padding: 20px 16px;
    justify-content: flex-start;
  }
`

const CardTitleDescriptionContainer = styled(Box)`
  @media screen and (max-width: 1000px) {
    gap: 12px;
    flex: unset;
  }
`

const CardTitle = styled.div`
  color: ${({ theme }) => theme.colors.brandPrimaryText};
  font-family: 'Space Grotesk';
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;

  @media screen and (max-width: 1000px) {
    font-size: 26px;
  }
`

interface StatsProps {
  stats: Stat[]
  borderColor?: string
  nameColor?: string
  valueColor?: string
}

export const Stats = ({
  stats,
  borderColor,
  nameColor,
  valueColor,
}: StatsProps) =>
  stats?.length ? (
    <>
      {stats.map(({ name, value }, index) => (
        <StatWrapper key={name} borderColor={borderColor}>
          <StatTitle nameColor={nameColor}>{name}</StatTitle>
          <StatValue valueColor={valueColor}>{value}</StatValue>
        </StatWrapper>
      ))}
    </>
  ) : (
    <></>
  )

interface FeaturedProjectProps {
  timeLeft: string
  cardImageSrc: string
  coinName: string
  coinTicker: string
  chainIcon: string
  logoUrl?: string
  ratio: string
  descriptionText: string
  stats: Stat[]
  address: string
  media?: Media
  cmsData: IPool
  isTimer: boolean
  slug: string
}

type Stat = {
  name: string
  value: string
}

const FeaturedProjectCard = ({
  timeLeft,
  coinName,
  logoUrl,
  descriptionText,
  ratio,
  stats,
  media,
  cmsData,
  isTimer,
  slug,
}: FeaturedProjectProps) => {
  const [percentage, setPercentage] = useState<number>(0)
  const [percentageString, setPercentageString] = useState('0')
  const [timerString, setTimerString] = useState('')
  const [shouldShowTimer, setShouldShowTimer] = useState(false)

  const history = useHistory()
  const viewDetailPool = () =>
    history.push(routes.poolDetails.index + `/${slug}`)

  const [isLive, setIsLive] = useState(timeLeft === 'live')
  const timer = useCountdownTimer(
    cmsData?.privateSale?.start ?? new Date().toISOString(),
  )

  const { blastProvider } = useWeb3()

  const calculateProgress = useCallback(async () => {
    try {
      const isValid = ethers.isAddress(cmsData?.poolAddress)

      if (!isValid || !blastProvider) return

      const contract = new ethers.Contract(
        cmsData?.poolAddress,
        TokenSale.abi as ethers.InterfaceAbi,
        blastProvider,
      )
      const tokensSold = await contract?.totalTokenSold()
      const sold = new Big(
        amountFromWei(tokensSold, USDB_DECIMALS).toString(),
      ).div(cmsData?.privateSale.price)
      const softCap = cmsData?.poolSettings?.totalSupplyForSale

      setPercentage(countProgress(sold, new Big(softCap)) ?? 0)
      const pctVal = String((Number(sold.toNumber()) * 100) / Number(softCap))

      setPercentageString(String(pctVal))
      setShouldShowTimer(true)
    } catch (error) {
      console.error(error)
    }
  }, [blastProvider, cmsData])

  useEffect(() => {
    if (shouldShowTimer) {
      const interval = setInterval(() => {
        const timeLeft = calculateTimeLeft(cmsData?.privateSale?.end)
        setTimerString(timeLeft)
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [shouldShowTimer, cmsData?.privateSale?.end])

  useEffect(() => {
    if ((isTimer || timeLeft === 'live') && !isLive) {
      setIsLive(true)
      return
    }
    if (isLive) calculateProgress()
  }, [timer, isLive, media, timeLeft, calculateProgress, isTimer])

  const to = `/fundraise/${slug}`

  return (
    <CardContainer ac g="6%" z={1} columnMobile>
      <CardImageContainer mw="662px">
        <StyledLink to={to}>
          <Image
            src={
              (media?.featuredProjectImage as any)?.url ??
              tempFeaturedProjectImage
            }
          />
          <OpeningLabel isLive={isLive} isTimer={!isLive && isTimer}>
            {isLive && <LiveLoaderCircle />}
            <span>
              {!isLive && isTimer ? timer : isLive ? 'live' : timeLeft}
            </span>
          </OpeningLabel>
          {isLive && (
            <ProgressContainer mw="590px">
              <Box justify="space-betwen" align="center">
                <Box direction="column" className="bar">
                  <Box justify="space-between">
                    <span className="title">RAISE PROGRESS</span>
                    <span className="pct">
                      {parseFloat(percentageString).toFixed(2)}%
                    </span>
                  </Box>
                  <ProgressBar
                    isThinner
                    percentage={percentage ? Math.min(percentage, 100) : 0}
                  />
                </Box>
                <Box
                  direction="column"
                  className="timer"
                  align="center"
                  justify="center"
                >
                  <span className="time-text">{timerString} LEFT</span>
                </Box>
              </Box>
            </ProgressContainer>
          )}
        </StyledLink>
      </CardImageContainer>
      <CardContentContainer dc c ac mw="100%">
        <CardTitleDescriptionContainer dc g={24}>
          <Box ac g={12}>
            <Link to={to}>
              <Logo src={logoUrl} />
            </Link>
            <Link to={to}>
              <CardTitle>{coinName}</CardTitle>
            </Link>
            {/* <TickerBox>
            {cmsData?.listingType && (
              <TickerText>{cmsData.listingType}</TickerText>
            )}
          </TickerBox> */}
          </Box>
          {/* <ConversionRateText>{ratio}</ConversionRateText> */}
          <Box>
            <Dotdotdot clamp={3}>
              <DescriptionText>
                {removeMd(
                  descriptionText ??
                    'Connects first-class international art world participants in a visionary, beautifully rendered digital world...',
                )}
              </DescriptionText>
            </Dotdotdot>
          </Box>
        </CardTitleDescriptionContainer>
        <StatsBox ac marginTop="10%" g="5%">
          <Stats stats={stats} />
        </StatsBox>
      </CardContentContainer>
    </CardContainer>
  )
}

const SectionCont = styled(Box)`
  padding: 55px 0 50px;
`

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.semantics.zap.surface.primary};
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 32px;
  font-style: normal;
  font-weight: 300;
  line-height: 25px; /* 78.125% */
  letter-spacing: 9.6px;
  text-transform: uppercase;
  z-index: 1;

  @media screen and (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`

export default function FeaturedProject({
  title = 'Featured project',
  project,
}: {
  title?: string
  project: FeaturedProjectProps | undefined
}) {
  return (
    <ForceFullWidth>
      <MaxWidth maxWidth={1400}>
        <SectionCont dc g={30} centerMobile>
          <SectionTitle>{title}</SectionTitle>
          {project ? <FeaturedProjectCard {...project} /> : <LoadingState />}
        </SectionCont>
      </MaxWidth>
    </ForceFullWidth>
  )
}
